// @flow
import type { Dispatch } from 'redux';
import { createSelector } from 'reselect';
import type { Selector as ReselectSelector } from 'reselect';
import invariant from '@rover/utilities/invariant';
import type { ActionTypes, EffectTypes, SelectorTypes, DuckOptions, Duck } from './types';

export const createDuck = <S: {}, A: ActionTypes, E: EffectTypes, C: SelectorTypes>({
  initialState,
  rootSelector,
  name,
  actions,
  effects,
  selectors,
  externalActionHandlers,
}: DuckOptions<S, A, E, C>): Duck<S, A, E, C> => {
  // Build the action creators using a namespaced
  // action type
  const duckActions: $Shape<A> = {};
  Object.keys(actions).forEach((type: string) => {
    const actionType = `${name}/${type}`;
    // $FlowFixMe
    duckActions[type] = Object.assign(
      (payload: any) => ({
        payload,
        type: actionType,
      }),
      {
        actionType,
        toString: () => actionType,
      }
    );
  });

  // Build selectors giving them access to the state and each other
  const duckSelectors: $Shape<C> = {};
  const selector = rootSelector || (s => s[name]);
  const getDuckState: ReselectSelector<any, void, S> = createSelector([selector], (state: S): S => {
    invariant(
      state,
      `You have a missing reducer for duck: ${name}.${'\n'}Register it in the page's <Provider> component. More information here: https://github.com/roverdotcom/web/blob/master/src/frontend/docs/platform/ducks/connecting-components-to-state-management.md#the-top-level-container`
    );
    return state;
  });
  Object.keys(selectors).forEach((type: $Keys<C>) => {
    duckSelectors[type] = selectors[type](getDuckState, createSelector, duckSelectors);
  });

  // Build the effects by passing them the internal
  // actions and effects
  const duckEffects: $Shape<E> = {};
  Object.keys(effects).forEach((type: $Keys<E>) => {
    duckEffects[type] = (payload: any) => (
      dispatch: Dispatch<any>,
      getState: () => any,
      extraArgs
    ) => {
      const effect = effects[type](payload);
      return effect(dispatch, getState, duckActions, duckEffects, duckSelectors, extraArgs);
    };
  });

  // Create the reducer
  const reducer = (state: S = initialState, action: { type: string, payload: ?{} }): S => {
    if (externalActionHandlers && externalActionHandlers[action.type]) {
      return externalActionHandlers[action.type](action)(state);
    }
    if (action.type.includes(name)) {
      const actionType: $Keys<A> = action.type.replace(`${name}/`, '');
      const reduce: ?(action: any) => (state: $ReadOnly<S>) => S = actions[actionType];
      if (reduce) {
        return reduce(action.payload)(state);
      }
    }
    return state;
  };

  return {
    actions: duckActions,
    effects: duckEffects,
    selectors: duckSelectors,
    reducer,
  };
};



// WEBPACK FOOTER //
// ../react-lib/src/lib/redux-duck/createDuck.js